import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { Receipt } from '@phosphor-icons/react';
import { formatCurrency } from '@lib/formatters';
import { getIntlLocale } from '@lib/locale';
import client from '@api/client';
import { VENUE_API } from '../api/endpoints';
import { useTranslation } from 'react-i18next';
import useOrderStore from '../stores/orderStore';
import { storage } from '../utils/storage';

const VenueOrderSummaryDialog = () => {
  const { t } = useTranslation('orders');
  const open = useOrderStore((s) => s.venueOrderDialogOpen);
  const venueOrderSummary = useOrderStore((s) => s.venueOrderSummary);
  const setVenueOrderDialogOpen = useOrderStore(
    (s) => s.setVenueOrderDialogOpen,
  );
  const onClose = () => setVenueOrderDialogOpen(false);
  const orderSummary = venueOrderSummary;
  const venueId = storage.getVenueId();
  const orderId =
    venueOrderSummary?.id || storage.getCurrentVenueOrderId();
  const [current, setCurrent] = useState(orderSummary || null);

  useEffect(() => {
    setCurrent(orderSummary || null);
  }, [orderSummary]);

  useEffect(() => {
    if (!open || !venueId || !orderId) return;

    let mounted = true;
    const fetchLatest = async () => {
      try {
        const res = await client.get(`${VENUE_API}/${venueId}/orders/${orderId}`);
        if (mounted) setCurrent(res.data);
      } catch {
        // Silently ignore — polling retries every 10s
      }
    };

    fetchLatest();
    const interval = setInterval(fetchLatest, 10000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [open, venueId, orderId]);

  if (!current) return null;

  const restaurantTotals = (current.restaurantOrders || []).map((ro) => {
    const subtotal =
      ro.subtotal ??
      (ro.items || []).reduce((sum, it) => sum + Number(it.totalPrice ?? 0), 0);
    return {
      name: ro.restaurant?.name || 'Restaurant',
      subtotal: Number(subtotal ?? 0),
    };
  });

  const totalSummary = Number(current.total ?? 0);

  const statusLabel = (s) => {
    switch (s) {
      case 'PLACED':
        return t('venueOrder.status.placed', 'Eingegangen');
      case 'COOKING':
        return t('venueOrder.status.cooking', 'In Zubereitung');
      case 'READY':
        return t('venueOrder.status.ready', 'Bereit');
      case 'COMPLETED':
        return t('venueOrder.status.completed', 'Abgeschlossen');
      case 'CANCELLED':
        return t('venueOrder.status.cancelled', 'Storniert');
      default:
        return s || '—';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1300 }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 5,
            borderRadius: 999,
            bgcolor: 'divider',
            mx: 'auto',
            mb: 1,
          }}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Receipt size={24} />
          {t('venueOrder.title', 'Venue-Bestellübersicht')}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            background: (t) => `linear-gradient(135deg, ${t.palette.background.paper}, ${t.palette.brand.forest})`,
            borderWidth: 1, borderStyle: 'solid', borderColor: 'divider',
            boxShadow: '0 12px 26px rgba(31,45,42,0.12)',
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={700}>{t('venueOrder.total', 'Gesamt')}</Typography>
            <Typography fontWeight={800}>
              {formatCurrency(Number(current.total ?? 0))}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('venueOrder.orderLabel', 'Bestellung')} {current.orderNumber ?? '—'} · {t('venueOrder.statusLabel', 'Status')}:{' '}
            {statusLabel(current.status)}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            bgcolor: 'background.paper',
            borderWidth: 1, borderStyle: 'solid', borderColor: 'divider',
          }}
        >
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            {t('venueOrder.details', 'Details')}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.type', 'Typ')}: {current.fulfillmentType ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.table', 'Tisch')}: {current.tableIdentifier ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.customer', 'Kunde')}: {current.customerName ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.phone', 'Telefon')}: {current.customerPhone ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.payment', 'Zahlung')}: {current.paymentMethod ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('venueOrder.orderedAt', 'Bestellt am')}:{' '}
            {current.placedAt
              ? new Date(current.placedAt).toLocaleString(getIntlLocale(), {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
              : '—'}
          </Typography>
        </Paper>

        <Box mt={2}>
          {current.restaurantOrders?.length > 0 ? (
            current.restaurantOrders.map((ro) => (
              <Paper
                key={ro.orderId || ro.orderNumber}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  borderWidth: 1, borderStyle: 'solid', borderColor: 'divider',
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary.main"
                >
                  {ro.restaurant?.name || 'Restaurant'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('venueOrder.statusLabel', 'Status')}: {statusLabel(ro.status)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {ro.items?.map((item, idx) => (
                  <Box key={item.id || idx} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {item.quantity}× {item.name}
                    </Typography>
                    {item.specialNotes && (
                      <Typography variant="caption" color="text.secondary">
                        {item.specialNotes}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('venueOrder.noOrders', 'Keine Restaurant-Bestellungen gefunden.')}
            </Typography>
          )}
        </Box>

        {restaurantTotals.length > 0 && (
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'background.paper',
              borderWidth: 1, borderStyle: 'solid', borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary.main"
            >
              {t('venueOrder.restaurantTotals', 'Summen je Restaurant')}
            </Typography>
            {restaurantTotals.map((r, idx) => (
              <Box
                key={`${r.name}-${idx}`}
                display="flex"
                justifyContent="space-between"
              >
                <Typography variant="body2">{r.name}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(r.subtotal)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight="bold">
                {t('venueOrder.grandTotal', 'Gesamtsumme')}
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {formatCurrency(totalSummary)}
              </Typography>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{
            bgcolor: 'primary.main',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {t('venueOrder.close', 'Schließen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueOrderSummaryDialog;
